import { useEffect, useState } from "react";
import { X } from "lucide-react";
import platformMapAsset from "@/assets/homepage-platform-map.png.asset.json";

const platformMap = platformMapAsset.url;

/**
 * "Everything in one place" platform map — a hand-illustrated overview of the
 * four pillars (Understand, Regulate, Process, Integrate), journeys and
 * community. Tap the image to open it full-size so the small text is readable.
 */
export function PlatformMapSection() {
  const [open, setOpen] = useState(false);

  // Lock background scroll while the zoomed view is open.
  useEffect(() => {
    if (!open || typeof window === "undefined") return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <section aria-labelledby="platform-map-heading" className="space-y-4">
      <header className="mx-auto max-w-2xl text-center">
        <h2
          id="platform-map-heading"
          className="font-display text-lg leading-tight tracking-tight text-foreground sm:text-2xl"
        >
          Everything in One Place
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
          A little map of Recovery Trauma™ — every tool, journal, journey and
          safe space, on one page. Tap the picture to see it up close.
        </p>
      </header>

      <div className="relative mx-auto w-full max-w-md">
        <div
          aria-hidden
          className="absolute inset-0 -m-4 rounded-[3rem] bg-[var(--gradient-gold)] opacity-15 blur-3xl"
        />
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open the Recovery Trauma™ platform map full size"
          className="group relative block w-full cursor-zoom-in overflow-hidden rounded-2xl border border-gold/25 bg-cream/40 shadow-[var(--shadow-soft)] transition-transform duration-200 hover:-translate-y-1 sm:rounded-[2rem]"
        >
          <img
            src={platformMap}
            alt="Illustrated map of Recovery Trauma™: four gentle columns — Understand, Regulate, Process and Integrate — listing the library, hub, journals, grounding tools, journeys, community and therapist support, with the words “A Safe Place to Learn, Recover and Grow” and “Recovery isn’t a straight line. Every step still counts.”"
            width={1024}
            height={1536}
            loading="lazy"
            decoding="async"
            className="h-auto w-full object-cover"
          />
        </button>
      </div>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Recovery Trauma™ platform map, full size"
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 p-3 sm:p-6"
          onClick={() => setOpen(false)}
        >
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close the platform map"
            className="absolute right-4 top-4 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full bg-background/90 text-foreground shadow-[var(--shadow-soft)]"
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={platformMap}
            alt="Full-size Recovery Trauma™ platform map"
            width={1024}
            height={1536}
            className="max-h-full w-auto max-w-full rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}
